/* src/pages/Quarantine.jsx
 * Workspace quarantine: review spam/suspicious submissions and release held leads.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useToast } from '../contexts/ToastContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { adminApi, leadApi } from '../utils/api';
import { QuarantineListSkeleton } from '../components/Skeleton';

const STAT_TILES = [
  { key: 'total', label: 'TOTAL' },
  { key: 'clean', label: 'CLEAN' },
  { key: 'suspicious', label: 'SUSPICIOUS' },
  { key: 'spam', label: 'SPAM (HELD)' },
];

function statusChipClass(status) {
  if (status === 'spam') return 'inbox-chip danger';
  if (status === 'suspicious') return 'inbox-chip warn';
  return 'inbox-chip';
}

function alertChipClass(area) {
  if (!area) return 'inbox-chip';
  if (area.startsWith('tracking_drift_') || area.startsWith('guide_link_drift')) return 'inbox-chip warn';
  if (area === 'offline_conversion_dead') return 'inbox-chip danger';
  return 'inbox-chip';
}

function alertLabel(area) {
  if (!area) return 'unknown';
  if (area.startsWith('tracking_drift_')) return 'tracking drift';
  if (area === 'offline_conversion_dead') return 'dead-letter';
  if (area.startsWith('guide_link_drift')) return 'guide drift';
  return area.replace(/_/g, ' ');
}

function formatDate(timestamp) {
  if (!timestamp) return 'N/A';
  if (timestamp.toDate) return timestamp.toDate().toLocaleString();
  if (typeof timestamp._seconds === 'number') return new Date(timestamp._seconds * 1000).toLocaleString();
  if (typeof timestamp.seconds === 'number') return new Date(timestamp.seconds * 1000).toLocaleString();
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
}

function formatAlertTime(isoStr) {
  if (!isoStr) return 'N/A';
  try {
    return new Date(isoStr).toLocaleString();
  } catch {
    return isoStr;
  }
}

function matchesLogSearch(submission, q) {
  if (!q) return true;
  const lower = q.trim().toLowerCase();
  const data = submission.data || {};
  const dataStr = Object.keys(data)
    .concat(Object.values(data).map(String))
    .join(' ')
    .toLowerCase();
  return dataStr.includes(lower);
}

function inboxHref(submission, conversationBySubmission) {
  const conv = conversationBySubmission[submission.id];
  if (conv?.formId) return `/leads/${conv.formId}?conversation=${conv.id}`;
  if (conv?.id) return `/leads?conversation=${conv.id}`;
  return '/leads';
}

function QuarantineRow({
  submission,
  formName,
  conversationBySubmission,
  releasingId,
  onRelease,
}) {
  const isSpam = submission.spamStatus === 'spam';
  const isSuspicious = submission.spamStatus === 'suspicious';
  const reasons = submission.reasons || [];
  const methods = submission.methods || [];

  return (
    <div className="quarantine-row">
      <div className="quarantine-row-top">
        <div className="quarantine-row-meta">
          <span className="quarantine-row-date">{formatDate(submission.timestamp)}</span>
          <span className="quarantine-row-form">{formName}</span>
        </div>
        <div className="quarantine-row-badges">
          <span className={statusChipClass(submission.spamStatus)}>
            {(submission.spamStatus || 'unknown').toUpperCase()}
          </span>
          <span className="inbox-chip">SCORE {submission.spamScore ?? 0}</span>
        </div>
      </div>

      {isSpam && (
        <div className="quarantine-held-note">Held from Inbox. Release to promote as a normal lead.</div>
      )}
      {isSuspicious && (
        <div className="quarantine-held-note">Already in Inbox. Review there; no release needed.</div>
      )}

      <div className="quarantine-reasons">
        <span className="quarantine-reasons-label">Detection reasons</span>
        {reasons.length > 0 ? (
          <div className="quarantine-reasons-chips">
            {reasons.map((reason, i) => (
              <span key={i} className="inbox-chip">{reason}</span>
            ))}
          </div>
        ) : (
          <span className="quarantine-reasons-empty">No reason recorded.</span>
        )}
        {methods.length > 0 && (
          <div className="quarantine-methods">
            Methods: {methods.join(', ')}
          </div>
        )}
      </div>

      <details className="quarantine-data-details">
        <summary>View submission data</summary>
        <pre className="quarantine-data-pre">{JSON.stringify(submission.data, null, 2)}</pre>
      </details>

      {submission.ipAddress && (
        <div className="quarantine-ip">IP: {submission.ipAddress}</div>
      )}

      <div className="quarantine-row-actions">
        {isSpam && (
          <button
            type="button"
            className="inbox-pill-btn-primary"
            disabled={releasingId === submission.id}
            onClick={() => onRelease(submission)}
          >
            {releasingId === submission.id ? 'Releasing...' : 'Release'}
          </button>
        )}
        {isSuspicious && (
          <Link to={inboxHref(submission, conversationBySubmission)} className="inbox-pill-btn">
            View in Inbox
          </Link>
        )}
      </div>
    </div>
  );
}

export default function Quarantine() {
  const { currentUser } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const { showToast } = useToast();
  const isMobile = useIsMobile();

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedForm, setSelectedForm] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [forms, setForms] = useState([]);
  const [stats, setStats] = useState({ total: 0, clean: 0, suspicious: 0, spam: 0 });
  const [conversationBySubmission, setConversationBySubmission] = useState({});
  const [releasingId, setReleasingId] = useState(null);

  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [alertsError, setAlertsError] = useState(null);
  const [expandedAlertId, setExpandedAlertId] = useState(null);
  const [healthOpen, setHealthOpen] = useState(true);

  useEffect(() => {
    if (currentUser && selectedWorkspaceId) {
      (async () => {
        await loadForms();
        await loadSubmissions();
        await loadConversationMap();
      })();
    }
  }, [currentUser, selectedWorkspaceId, filter, selectedForm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentUser) loadAlerts();
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadConversationMap() {
    if (!selectedWorkspaceId) return;
    try {
      const result = await leadApi.getWorkspaceConversations(selectedWorkspaceId);
      const map = {};
      (result.conversations || []).forEach((c) => {
        if (c.submissionId) map[c.submissionId] = { id: c.id, formId: c.formId };
      });
      setConversationBySubmission(map);
    } catch (err) {
      console.error('Error loading conversations for inbox links:', err);
    }
  }

  async function loadAlerts() {
    setAlertsLoading(true);
    setAlertsError(null);
    try {
      const data = await adminApi.getSystemAlerts(50);
      if (data?.error) throw new Error(data.error);
      setAlerts(data?.alerts ?? []);
    } catch (err) {
      console.error('Error loading system alerts:', err);
      setAlertsError(err?.message ?? 'Failed to load alerts');
    } finally {
      setAlertsLoading(false);
    }
  }

  async function loadForms() {
    if (!selectedWorkspaceId) return [];
    try {
      const formsRef = collection(db, 'forms');
      const q = query(formsRef, where('workspaceId', '==', selectedWorkspaceId));
      const snapshot = await getDocs(q);
      const formsList = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setForms(formsList);
      return formsList;
    } catch (error) {
      console.error('Error loading forms:', error);
      return [];
    }
  }

  async function handleRefresh() {
    await Promise.all([loadForms(), loadSubmissions(), loadConversationMap()]);
  }

  async function loadSubmissions() {
    if (!selectedWorkspaceId) return;

    setLoading(true);
    try {
      const result = await leadApi.getWorkspaceSubmissions(selectedWorkspaceId, {
        ...(selectedForm !== 'all' ? { formId: selectedForm } : {}),
      });
      if (result?.error) throw new Error(result.error);
      const list = result.submissions || [];
      setStats(result.stats || { total: 0, clean: 0, suspicious: 0, spam: 0 });
      setSubmissions(filter !== 'all' ? list.filter((s) => s.spamStatus === filter) : list);
    } catch (error) {
      console.error('Error loading submissions:', error);
      showToast('Error loading submissions. Check console for details.', 'error');
    } finally {
      setLoading(false);
    }
  }

  async function handleRelease(submission) {
    if (!window.confirm('Release this lead to your Inbox?')) return;

    const prevSubmissions = submissions;
    setReleasingId(submission.id);
    setSubmissions((s) => s.filter((x) => x.id !== submission.id));

    try {
      await leadApi.releaseFromQuarantine(submission.id);
      showToast('Released to Inbox', 'success');
    } catch (err) {
      setSubmissions(prevSubmissions);
      showToast(err?.message || 'Release failed', 'error');
    } finally {
      setReleasingId(null);
    }
  }

  const filteredSubmissions = submissions.filter((s) => matchesLogSearch(s, searchQuery));
  const pagePad = isMobile ? '1rem 1rem 2rem' : '24px 32px 48px';

  return (
    <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
      <div className="today-page quarantine-page" style={{ padding: pagePad }}>
        <div className="today-header">
          <div className="today-header-left">
            <h1 className="today-h1">Quarantine</h1>
          </div>
        </div>

        <div
          className="today-stat-strip"
          style={{
            gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
          }}
        >
          {STAT_TILES.map((tile) => (
            <div key={tile.key} className="today-stat-card">
              <div className="today-stat-label">{tile.label}</div>
              <div className="today-stat-value">{loading ? '—' : stats[tile.key]}</div>
            </div>
          ))}
        </div>

        <div className="quarantine-toolbar">
          <label className="quarantine-field">
            <span className="quarantine-field-label">Status</span>
            <select
              className="fb-select quarantine-select"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="clean">Clean</option>
              <option value="suspicious">Suspicious</option>
              <option value="spam">Spam</option>
            </select>
          </label>
          <label className="quarantine-field">
            <span className="quarantine-field-label">Form</span>
            <select
              className="fb-select quarantine-select"
              value={selectedForm}
              onChange={(e) => setSelectedForm(e.target.value)}
            >
              <option value="all">All Forms</option>
              {forms.map((form) => (
                <option key={form.id} value={form.id}>{form.name}</option>
              ))}
            </select>
          </label>
          <button type="button" className="inbox-pill-btn" onClick={handleRefresh}>
            Refresh
          </button>
        </div>

        <div className="quarantine-search-row">
          <input
            type="search"
            className="fb-input quarantine-search"
            placeholder="Search by name, email, or form data..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search submissions"
          />
          {searchQuery && (
            <span className="quarantine-search-count">
              {filteredSubmissions.length} of {submissions.length} submission{submissions.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        <div className="today-panel quarantine-list-panel">
          <div className="today-panel-header">
            <span className="today-panel-title">Submissions</span>
          </div>
          <div className="today-panel-body">
            {loading ? (
              <QuarantineListSkeleton />
            ) : submissions.length === 0 ? (
              <div className="today-empty">
                No submissions found.{filter !== 'all' ? ` Filtered by: ${filter}.` : ''}
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <div className="today-empty">
                No submissions match &quot;{searchQuery}&quot;. Try a different search.
              </div>
            ) : (
              filteredSubmissions.map((submission) => (
                <QuarantineRow
                  key={submission.id}
                  submission={submission}
                  formName={forms.find((f) => f.id === submission.formId)?.name || submission.formId}
                  conversationBySubmission={conversationBySubmission}
                  releasingId={releasingId}
                  onRelease={handleRelease}
                />
              ))
            )}
          </div>
        </div>

        <div className="today-panel quarantine-info-panel">
          <div className="today-panel-header">
            <span className="today-panel-title">How it works</span>
          </div>
          <div className="today-panel-body quarantine-info-body">
            <ul className="quarantine-info-list">
              <li><strong>Clean</strong> (score under 30): Normal leads, stored for audit.</li>
              <li><strong>Suspicious</strong> (score 30-69): Already in your Inbox, flagged for review.</li>
              <li><strong>Spam</strong> (score 70+): Held here, never reached the Inbox. Release to promote quietly.</li>
              <li><strong>AI detection:</strong> Gemini scores each submission using your business context and knowledge base.</li>
              <li><strong>Release:</strong> Creates the lead in your Inbox without re-firing notifications or auto-replies.</li>
            </ul>
          </div>
        </div>

        <div className="today-panel quarantine-health-panel">
          <button
            type="button"
            className="quarantine-health-toggle"
            onClick={() => setHealthOpen((o) => !o)}
          >
            <span className="quarantine-health-toggle-left">
              <span className="today-panel-title">System Health</span>
              {!alertsLoading && (
                <span className={alerts.length > 0 ? 'inbox-chip warn' : 'inbox-chip accent-soft'}>
                  {alerts.length > 0 ? `${alerts.length} ALERT${alerts.length !== 1 ? 'S' : ''}` : 'HEALTHY'}
                </span>
              )}
            </span>
            <span className="quarantine-health-chevron">{healthOpen ? '\u25b2' : '\u25bc'}</span>
          </button>

          {healthOpen && (
            <div className="today-panel-body">
              {alertsLoading ? (
                <div className="today-empty">Loading alerts...</div>
              ) : alertsError ? (
                <div className="today-load-error">
                  Error loading alerts: {alertsError}
                  <button type="button" className="inbox-pill-btn" onClick={loadAlerts} style={{ marginLeft: '0.75rem' }}>
                    Retry
                  </button>
                </div>
              ) : alerts.length === 0 ? (
                <div className="today-empty" style={{ color: 'var(--accent-ink)' }}>
                  No system alerts. Everything healthy.
                </div>
              ) : (
                <>
                  <div className="quarantine-alerts-list">
                    {alerts.map((alert) => {
                      const isExpanded = expandedAlertId === alert.id;
                      return (
                        <div key={alert.id} className="quarantine-alert-row">
                          <div className="quarantine-alert-top">
                            <span className="quarantine-alert-time">{formatAlertTime(alert.at)}</span>
                            <span className={alertChipClass(alert.area)}>{alertLabel(alert.area).toUpperCase()}</span>
                          </div>
                          <div className="quarantine-alert-summary">{alert.summary}</div>
                          {alert.detail && (
                            <>
                              <button
                                type="button"
                                className="inbox-pill-btn"
                                onClick={() => setExpandedAlertId(isExpanded ? null : alert.id)}
                              >
                                {isExpanded ? 'Hide detail' : 'View detail'}
                              </button>
                              {isExpanded && (
                                <pre className="quarantine-data-pre">{JSON.stringify(alert.detail, null, 2)}</pre>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="quarantine-alerts-footer">
                    Showing {alerts.length} most recent alert{alerts.length !== 1 ? 's' : ''}
                    {' · '}
                    <button type="button" className="today-link-accent" onClick={loadAlerts}>
                      Refresh
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
